// @ts-nocheck
'use client'

import React, { useState, useEffect } from'react'
import { useRouter } from'next/navigation'
import { createClient } from'@/lib/supabase/client'
import { useTheme, useAccentColor, useFontFamily } from'@/contexts/preferences-context'
import { InstrumentedButton } from'@/components/ui/instrumented-button'
import { Button } from'@/components/ui/button'
import { Input } from'@/components/ui/input'
import { Label } from'@/components/ui/label'
import { Textarea } from'@/components/ui/textarea'
import { useInstrumentedAction } from'@/hooks/useInstrumentedAction'
import { Tabs } from'@/components/ui/tabs'
import { MultiSelect } from'@/components/ui/multi-select'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from'@/components/ui/select'
import { Badge } from'@/components/ui/badge'
import {
  Wand2,
  BookOpen,
  TreePine,
  Sparkles,
  Loader2,
  AlertCircle,
  Settings,
  CheckCircle,
  Eye,
  EyeOff
} from'lucide-react'
import { ALLOWED_GENRES, ESTIMATED_CREDIT_COSTS, GENRE_DATA } from'@/lib/utils/constants'
import { STATIC_GENRE_IMAGES } from'@/lib/utils/genreImageSelector'
import StoryFoundationSection from'@/components/stories/create/StoryFoundationSection'
import WritingStyleSection from'@/components/stories/create/WritingStyleSection'
import { StepSection } from'@/components/stories/create/StepSection'
import { InfoTip } from'@/components/stories/create/InfoTip'
import { StoryPreferences } from'@/lib/types/preferences'
import { ThemeToggle } from'@/components/common/ThemeToggle'
import { toast } from'sonner'
import { cn } from'@/lib/utils/cn'
import { AppErrorBoundary } from'@/components/common/AppErrorBoundary'
import { FullscreenWizard, type WizardStep } from'@/components/ui/FullscreenWizard'
import {
 Dialog,
 DialogContent,
 DialogDescription,
 DialogFooter,
 DialogHeader,
 DialogTitle
} from'@/components/ui/dialog'

interface UserProfile {
 id: string
 username?: string
 email?: string
 subscription_tier: string
 credits_balance: number
 is_admin: boolean
 created_at: string
 updated_at: string
}

type CreationMode ='novel' |'choice-book'

interface FormData {
 title: string
 genres: string[]
 premise: string
 target_chapters: number
 target_length: string
 target_chapter_length: string
 target_audience: string
}

export default function CreateStoryPage() {
 const [userProfile, setUserProfile] = useState<UserProfile | null>(null)
 const [loading, setLoading] = useState(true)
 const [isSubmitting, setIsSubmitting] = useState(false)
 const [isCreating, setIsCreating] = useState(false)
 const [error, setError] = useState<string | null>(null)
 const [activeTab, setActiveTab] = useState<CreationMode>('novel')
 const [currentStep, setCurrentStep] = useState(0)
  const { theme, setTheme, actualTheme } = useTheme()
 const { accentColor } = useAccentColor()
 const { fontFamily } = useFontFamily()
 const router = useRouter()
 const supabase = createClient()

  const accentButton ='accent-bg-primary text-[var(--color-text-on-accent)] hover:accent-bg-secondary'
 // Form state
 const [formData, setFormData] = useState<FormData>({
 title:'',
 genres: [],
 premise:'',
 target_chapters: 10,
 target_length:'short',
 target_chapter_length:'medium',
 target_audience:'adult',
 })

 // Story preferences state
 const [preferences, setPreferences] = useState({
 plot_focus: 3,
 character_focus: 3,
 worldbuilding_depth: 3,
 theme_focus: 3,
 tone: 3,
 pacing: 3
 })

 // Genre selection state
 const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
 const [primaryGenre, setPrimaryGenre] = useState<string | null>(null)
 
 // Story ideas state
 interface StoryIdeaSuggestion {
 title: string
 premise: string
 hook: string
 display: string
 }

 const [storyIdeas, setStoryIdeas] = useState<StoryIdeaSuggestion[]>([]);
 const [generatingIdeas, setGeneratingIdeas] = useState(false);
 const [ideaGenerationCost, setIdeaGenerationCost] = useState<number>(0);
 const [showConfirmDialog, setShowConfirmDialog] = useState(false);
 
  // Genre images state
  const [genreImages, setGenreImages] = useState<Record<string, string>>({});
  const [showGenreGlow, setShowGenreGlow] = useState(true)
  
  // Tips and hints state
  const [tipsEnabled, setTipsEnabled] = useState(true)
  const [hasSeenGenreTips, setHasSeenGenreTips] = useState(false)
  const [showPrimaryHint, setShowPrimaryHint] = useState(false)
  const [showSuccessMessage, setShowSuccessMessage] = useState(false)

 const backgroundImageStyle: React.CSSProperties = {
 backgroundImage:'url("/victorian-street-scene.jpg")',
 backgroundSize:'cover',
 backgroundPosition:'center',
 backgroundRepeat:'no-repeat',
 backgroundAttachment:'fixed'
 };

 const renderBackgroundLayers = () => (
 <>
 <div className="absolute inset-0 z-0" style={backgroundImageStyle} />
 <div className="absolute inset-0 z-10 pointer-events-none bg-gradient-to-b from-theme-bg-primary/20 via-theme-bg-primary/15 to-theme-bg-primary/25" style={{ backgroundAttachment:'fixed' }} />
 </>
 );

 useEffect(() => {
 async function loadUser() {
 try {
 const { data: { session }, error } = await supabase.auth.getSession()
 
 if (error || !session?.user) {
 router.push('/auth/signin?redirect=/stories/create')
 return
 }

 const { data: profile } = await supabase
 .from('profiles')
 .select('*')
 .eq('id', session.user.id)
 .single()

 if (!profile) {
 router.push('/auth/create-profile')
 return
 }

 setUserProfile(profile)
 } catch (err) {
 console.error('Error loading user:', err)
 router.push('/auth/signin?redirect=/stories/create')
 } finally {
 setLoading(false)
 }
 }

 loadUser()
 }, [router, supabase])

  useEffect(() => {
    const timer = window.setTimeout(() => setShowGenreGlow(false), 2200)
    return () => window.clearTimeout(timer)
  }, [])

  // Load genre images - Using STATIC mapping for instant load (no API calls!)
  useEffect(() => {
    // Use pre-mapped static images for instant loading
    setGenreImages(STATIC_GENRE_IMAGES);
  }, [])

  // Load tips preferences from localStorage
  useEffect(() => {
    const tipsDisabled = localStorage.getItem('infinite-pages-tips-disabled') === 'true'
    const tipsSeen = localStorage.getItem('infinite-pages-genre-tips-seen') === 'true'
    
    setTipsEnabled(!tipsDisabled)
    setHasSeenGenreTips(tipsSeen)
    
    // Show primary hint if tips enabled and not seen before
    if (!tipsDisabled && !tipsSeen && selectedGenres.length === 0) {
      setShowPrimaryHint(true)
    }
  }, [])

  // Handle genre selection changes for contextual hints
  useEffect(() => {
    if (!tipsEnabled || hasSeenGenreTips) {
      setShowPrimaryHint(false)
      setShowSuccessMessage(false)
      return
    }
    
    if (selectedGenres.length === 0) {
      setShowPrimaryHint(true)
      setShowSuccessMessage(false)
    } else if (selectedGenres.length === 1) {
      setShowPrimaryHint(false)
      setShowSuccessMessage(true)
      // Mark as seen
      localStorage.setItem('infinite-pages-genre-tips-seen', 'true')
      setHasSeenGenreTips(true)
      // Auto-dismiss after 3 seconds
      const timer = setTimeout(() => setShowSuccessMessage(false), 3000)
      return () => clearTimeout(timer)
    } else {
      setShowPrimaryHint(false)
      setShowSuccessMessage(false)
    }
  }, [selectedGenres.length, tipsEnabled, hasSeenGenreTips])

  // Toggle tips function
  const toggleTips = () => {
    const newValue = !tipsEnabled
    setTipsEnabled(newValue)
    localStorage.setItem('infinite-pages-tips-disabled', String(!newValue))
    // Hide hints when disabling
    if (!newValue) {
      setShowPrimaryHint(false)
      setShowSuccessMessage(false)
    } else if (selectedGenres.length === 0 && !hasSeenGenreTips) {
      // Show hint again if re-enabling and no genres selected
      setShowPrimaryHint(true)
    }
  }

 const handlePreferenceChange = (key: string, value: number) => {
 setPreferences((prev) => ({ ...prev, [key]: value }))
 }

 const handleInputChange = (field: string, value: unknown) => {
 setFormData(prev => ({ ...prev, [field]: value }))
 }

 function handleGenreChange(genres: string[], primary: string | null) {
 setSelectedGenres(genres);
 setPrimaryGenre(primary);
 }

 const generateStoryIdeas = async () => {
 if (selectedGenres.length === 0) {
 setError('Please select at least one genre to generate story ideas')
 return
 }

 // If ideas already exist, show confirmation dialog
 if (storyIdeas.length > 0) {
 setShowConfirmDialog(true)
 return
 }

 // Proceed with generation
 await executeGenerateIdeas()
 }

 const executeGenerateIdeas = async () => {
 if (selectedGenres.length === 0) {
 setError('Please select at least one genre to generate story ideas')
 return
 }

 setShowConfirmDialog(false)
 setGeneratingIdeas(true)
 setError(null)

 try {
 const creativitySettings = {
 universe_adherence: preferences.plot_focus ?? 3,
 character_uniqueness: preferences.character_focus ?? 3,
 worldbuilding_depth: preferences.worldbuilding_depth ?? 3,
 genre_strictness: preferences.theme_focus ?? 3
 }

 const response = await fetch('/api/stories/generate-ideas', {
 method:'POST',
 credentials:'include',
 headers: {
'Content-Type':'application/json'
 },
 body: JSON.stringify({
 genres: selectedGenres,
 creativitySettings
 })
 })

 if (!response.ok) {
 let message ='Failed to generate story ideas. Please try again.'
 try {
 const errorPayload = await response.json()
 if (typeof errorPayload?.error ==='string' && errorPayload.error.trim() !=='') {
 message = errorPayload.error
 }
 } catch {
 // ignore parse error; default message stands
 }
 throw new Error(message)
 }

 const data = await response.json()
 if (!data.ideas || !Array.isArray(data.ideas)) {
 throw new Error('Invalid response format from server')
 }

 const suggestions: StoryIdeaSuggestion[] = data.ideas.map((idea: StoryIdeaSuggestion) => ({
 title: idea.title?.trim() ||'Untitled Story',
 premise: idea.premise?.trim() ||'',
 hook: idea.hook?.trim() ||'',
 display: idea.display?.trim() ||''
 }))

 // Clear old ideas and set new ones
 setStoryIdeas(suggestions)
 setIdeaGenerationCost(data.cost || 0)
 toast.success('✨ New story ideas generated!')
 } catch (error) {
 console.error('❌ Error generating story ideas:', error)
 const message =
 error instanceof Error && error.message.trim() !==''
 ? error.message
 :'Failed to generate story ideas. Please try again.'
 setError(message)
 toast.error(message)
 } finally {
 setGeneratingIdeas(false)
 }
 }

 const handleConfirmGenerate = () => {
 executeGenerateIdeas()
 }

 const handleCancelGenerate = () => {
 setShowConfirmDialog(false)
 }

 const handleTargetLengthChange = (length: string) => {
 let chapters: number
 switch (length) {
 case'short':
 chapters = 5
 break
 case'medium':
 chapters = 15
 break
 case'long':
 chapters = 30
 break
 default:
 chapters = 10
 }
 setFormData(prev => ({ 
 ...prev, 
 target_length: length,
 target_chapters: chapters
 }))
 setError(null)
 }

 const handleTabChange = (tab: CreationMode) => {
 // Don't allow switching to choice-book (coming soon)
 if (tab ==='choice-book') {
 return
 }
 
 setActiveTab(tab)
 setError(null)
 }

 // Instrumented handlers
 const instrumentedNavigateSettings = useInstrumentedAction(
 () => { window.location.href ='/settings' },
 { eventName:'story.create.settings', component:'create-page' }
 );

 const instrumentedTabToggle = useInstrumentedAction(
 (tab: CreationMode) => handleTabChange(tab),
 { eventName:'story.create.tab_toggle', component:'create-page' }
 );

 const instrumentedGenreSelect = useInstrumentedAction(
 (genre: string) => {
 if (selectedGenres.includes(genre)) {
 setSelectedGenres(prev => prev.filter(g => g !== genre))
 setPrimaryGenre(selectedGenres.filter(g => g !== genre)[0] || null)
 } else if (selectedGenres.length < 5) {
 setSelectedGenres(prev => [...prev, genre])
 if (!primaryGenre) {
 setPrimaryGenre(genre)
 }
 }
 },
 { eventName:'story.create.genre_select', component:'create-page' }
 );

 const instrumentedIdeaSelect = useInstrumentedAction(
 (idea: Record<string, unknown>) => {
 handleInputChange('title', idea.title)
 const combinedPremise = idea.hook
 ? `${idea.premise}\n\n✨ ${idea.hook}`
 : idea.premise
 handleInputChange('premise', combinedPremise)
 document.getElementById('premise')?.focus()
 },
 { eventName:'story.create.idea_select', component:'create-page' }
 );

 const instrumentedGenerateIdeas = useInstrumentedAction(
 generateStoryIdeas,
 { eventName:'story.create.generate_ideas', component:'create-page', errorMessage:'Failed to generate story ideas' }
 );

 const calculateEstimatedCost = (): number => {
 return ESTIMATED_CREDIT_COSTS.STORY_FOUNDATION + (ESTIMATED_CREDIT_COSTS.CHAPTER_GENERATION * formData.target_chapters)
 }

 // Convert string length values to numeric word counts for API
 const convertLengthToWords = (length: string): number => {
 switch (length) {
 case'short':
 return 5000
 case'medium':
 return 30000
 case'long':
 return 100000
 default:
 return 30000
 }
 }

 const convertChapterLengthToWords = (length: string): number => {
 switch (length) {
 case'short':
 return 750 // 500-1000 words
 case'medium':
 return 1750 // 1000-2500 words
 case'long':
 return 3500 // 2500+ words
 default:
 return 1750
 }
 }

 // Security: Check for suspicious patterns that might be code injection attempts
 const checkForSuspiciousContent = (text: string): string | null => {
 // Check for HTML/Script tags
 if (/<script|<\/script|<iframe|javascript:/gi.test(text)) {
 return'Your input contains HTML or script tags which are not allowed. Please remove any code and try again.'
 }

 // Check for common prompt injection attempts
 if (/ignore.{0,20}(previous|above|all).{0,20}instructions/gi.test(text)) {
 return'Your input contains patterns that look like instructions. Please write your story premise naturally.'
 }

 // Check for excessive special characters (might be code)
 // Use percentage-based threshold for longer texts to avoid false positives
 const specialCharCount = (text.match(/[{}<>[\]();]/g) || []).length
 const textLength = text.length
 const specialCharRatio = specialCharCount / textLength

 // If more than 5% of the text is special characters, it might be code
 // Also enforce a minimum threshold to avoid false positives on short text
 if (specialCharCount > 50 && specialCharRatio > 0.05) {
 return'Your input contains too many special characters. Please write your story in plain text.'
 }

 // Check for SQL-like patterns
 if (/\b(SELECT|INSERT|UPDATE|DELETE|DROP|CREATE)\s+(FROM|INTO|TABLE)/gi.test(text)) {
 return'Your input looks like database code. Please write your story premise in natural language.'
 }

 return null
 }

 const validateForm = (): boolean => {
 if (!formData.title.trim()) {
 setError('Story title is required')
 return false
 }

 // Security check for title
 const titleSecurityIssue = checkForSuspiciousContent(formData.title)
 if (titleSecurityIssue) {
 setError(titleSecurityIssue)
 return false
 }

 if (selectedGenres.length === 0) {
 setError('Please select at least one genre')
 return false
 }

 if (!formData.premise.trim()) {
 setError('Story premise is required')
 return false
 }

 // Security check for premise
 const premiseSecurityIssue = checkForSuspiciousContent(formData.premise)
 if (premiseSecurityIssue) {
 setError(premiseSecurityIssue)
 return false
 }

 // Length validation
 if (formData.premise.length < 50) {
 setError('Story premise is too short (minimum 50 characters)')
 return false
 }

 if (formData.premise.length > 10000) {
 setError('Story premise is too long (maximum 10,000 characters)')
 return false
 }

 return true
 }

 const handleSubmit = async () => {
 if (!validateForm()) return

 const estimatedCost = calculateEstimatedCost()
 if ((userProfile?.credits_balance || 0) < estimatedCost) {
 setError('Insufficient credits. Please purchase more credits to continue.')
 return
 }

 setIsSubmitting(true)
 setIsCreating(true)
 setError(null)

 // Calculate actual target length based on chapters × chapter length
 const chapterLength = convertChapterLengthToWords(formData.target_chapter_length)
 const calculatedTargetLength = formData.target_chapters * chapterLength

 try {
 const storyResponse = await fetch('/api/stories', {
 method:'POST',
 headers: {
'Content-Type':'application/json'
 },
 body: JSON.stringify({
 title: formData.title,
 genre: primaryGenre || selectedGenres[0],
 genres: selectedGenres,
 premise: formData.premise,
 target_length: calculatedTargetLength,
 target_chapters: formData.target_chapters,
 target_chapter_length: chapterLength,
 target_audience: formData.target_audience,
 })
 })

 if (!storyResponse.ok) {
 let message ='Failed to create story. Please try again.'
 try {
 const errorPayload = await storyResponse.json()
 if (errorPayload?.details && Array.isArray(errorPayload.details)) {
 const parsed = errorPayload.details.map((detail: string) => {
 if (detail.includes('premise')) {
 if (detail.includes('at least')) return'Story premise is too short (minimum 50 characters)'
 if (detail.includes('not exceed')) return'Story premise is too long (maximum 10,000 characters)'
 }
 if (detail.includes('title') && detail.includes('not exceed')) {
 return'Story title is too long (maximum 200 characters)'
 }
 return detail
 })
 message = parsed.join('.')
 } else if (typeof errorPayload?.error ==='string' && errorPayload.error.trim() !=='') {
 message = errorPayload.error
 }
 } catch {
 // ignore parse error; message stays default
 }
 throw new Error(message)
 }

 const storyData = await storyResponse.json()

 try {
 const prefResponse = await fetch(`/api/stories/${storyData.story.id}/preferences`, {
 method:'POST',
 headers: {
'Content-Type':'application/json'
 },
 body: JSON.stringify({
 ...preferences,
 story_id: storyData.story.id,
 user_id: userProfile!.id
 })
 })

 if (!prefResponse.ok) {
 let message ='Failed to save preferences. Please try again.'
 try {
 const errorPayload = await prefResponse.json()
 if (typeof errorPayload?.error ==='string' && errorPayload.error.trim() !=='') {
 message = errorPayload.error
 }
 } catch {
 // ignore parse error; use default message
 }
 throw new Error(message)
 }
 } catch (preferencesError) {
 const message =
 preferencesError instanceof Error && preferencesError.message.trim() !==''
 ? preferencesError.message
 :'Failed to save preferences. Please try again.'
 toast.error(message)
 }

 setError(null)
 setIsSubmitting(false)
 toast.success('✨ Story created successfully!')

 await new Promise(resolve => setTimeout(resolve, 1500))
 router.push(`/stories/${storyData.story.id}`)
 } catch (error) {
 console.error('❌ Error creating story:', error)
 const message =
 error instanceof Error && error.message.trim() !==''
 ? error.message
 :'Failed to create story. Please try again.'
 setError(message)
 toast.error(message)
 } finally {
 setIsSubmitting(false)
 setIsCreating(false)
 }
 }

 const instrumentedSubmit = useInstrumentedAction(
 handleSubmit,
 { eventName:'story.create.submit', component:'create-page', errorMessage:'Failed to create story' }
 );

 const getTabConfig = () => {
 switch (activeTab) {
 case'novel':
 return {
 title:'Novel',
 description:'Build a full-length novel with multiple chapters and complex plotlines',
 defaultChapters: 30,
 defaultLength:'long'
 }
 case'choice-book':
 return {
 title:'Choice Book',
 description:'Create an interactive story where readers make decisions that affect the plot',
 defaultChapters: 15,
 defaultLength:'medium'
 }
 default:
 return {
 title:'Novel',
 description:'Build a full-length novel with multiple chapters and complex plotlines',
 defaultChapters: 30,
 defaultLength:'long'
 }
 }
 }

 const tabConfig = getTabConfig()

 // Step completion logic: Steps are complete when user reaches them (previous step complete)
 // Users can use default values - they don't need to change preferences to complete steps
 const hasStoryDetails = formData.title.trim().length > 0 && formData.premise.trim().length > 0
 const genreComplete = selectedGenres.length > 0
 // Step 2 (Shape Your Story): Complete if genres are selected (sliders all have defaults, no validation needed)
 const shapeComplete = genreComplete
 // Step 3 (Story Details): Complete if shape is complete AND title + premise are filled
 const storyDetailsComplete = shapeComplete && hasStoryDetails
 // Step 4 (Length): Complete if story details are complete (section is accessible)
 // Users can use default values - completion doesn't require changing from defaults
 const lengthComplete = storyDetailsComplete
 const totalSteps = 4
 const completedSteps = [genreComplete, shapeComplete, storyDetailsComplete, lengthComplete].filter(Boolean).length
 const estimatedCost = calculateEstimatedCost()
 const balanceAfter = (userProfile?.credits_balance ?? 0) - estimatedCost

 if (loading) {
 return (
 <AppErrorBoundary source="create-story-page">
 <div className="relative min-h-screen overflow-hidden">
 {renderBackgroundLayers()}
 <div className="relative z-20 flex items-center justify-center min-h-screen">
 <div className="text-xl flex items-center gap-3 text-theme-text-primary">
 <Loader2 className="h-6 w-6 animate-spin" />
 Loading...
 </div>
 </div>
 </div>
 </AppErrorBoundary>
 )
 }

 if (!userProfile) {
 return (
 <AppErrorBoundary source="create-story-page">
 <div className="relative min-h-screen overflow-hidden">
 {renderBackgroundLayers()}
 <div className="relative z-20 flex items-center justify-center min-h-screen">
 <div className="text-xl flex items-center gap-3 text-theme-text-primary">
 <AlertCircle className="h-6 w-6" />
 Authentication required
 </div>
 </div>
 </div>
 </AppErrorBoundary>
 )
 }

 // Create wizard steps
 const wizardSteps: WizardStep[] = [
   {
     id: 1,
     title: 'Select Your Genres',
     description: tipsEnabled
       ? 'Choose your PRIMARY genre first - this sets the core tone of your story. Add up to 4 more genres to blend in stylistic flavor.'
       : 'Select up to 5 genres. Your first selection becomes the primary genre.',
     content: (
       <div className="space-y-4">
         {/* Tips toggle */}
         <div className="flex items-center justify-end">
           <button
             onClick={toggleTips}
             className="flex items-center gap-1.5 text-sm text-theme-text-secondary hover:text-theme-text-primary transition-colors"
             aria-label={tipsEnabled ? 'Hide tips' : 'Show tips'}
           >
             {tipsEnabled ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
             <span>{tipsEnabled ? 'Hide tips' : 'Show tips'}</span>
           </button>
         </div>

         {/* Floating hint for first genre */}
         {showPrimaryHint && tipsEnabled && !hasSeenGenreTips && selectedGenres.length === 0 && (
           <div className="relative -mt-2 mb-4">
             <div className="absolute left-0 right-0 -top-12 flex justify-center z-20 pointer-events-none">
               <div className="bg-theme-bg-elevated border border-theme-border-primary rounded-lg px-4 py-2 shadow-lg">
                 <p className="text-sm text-theme-text-primary flex items-center gap-2">
                   <span>👆</span>
                   <span>Start here - select your primary genre</span>
                 </p>
               </div>
             </div>
           </div>
         )}

          {/* Genre cards grid */}
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
           {ALLOWED_GENRES.map((genre, index) => {
             const isSelected = selectedGenres.includes(genre)
             const isPrimary = selectedGenres.length > 0 && selectedGenres[0] === genre
             const selectionIndex = selectedGenres.indexOf(genre)
             const isFirstCard = index === 0
             const shouldPulse = isFirstCard && showPrimaryHint && tipsEnabled && !hasSeenGenreTips && selectedGenres.length === 0

             const genreInfo = GENRE_DATA[genre as keyof typeof GENRE_DATA] || {
               description: `A ${genre} story with unique elements`,
               subcategories: []
             }
             const imagePath = genreImages[genre] || '/victorian-street-scene.jpg'

             return (
               <button
                 key={genre}
                 type="button"
                 onClick={() => instrumentedGenreSelect(genre)}
                 disabled={!isSelected && selectedGenres.length >= 5}
                 className={cn(
                   'relative min-h-[300px] overflow-hidden rounded-xl p-4 text-left transition-all duration-300',
                   isPrimary
                     ? 'border-4 accent-border-primary ring-4 ring-[var(--accent-primary)] scale-105 shadow-2xl shadow-[0_0_32px_rgba(0,0,0,0.25)]'
                     : isSelected
                       ? 'border-2 accent-border-secondary shadow-lg'
                       : 'border-theme-border-card hover:border-theme-border-secondary',
                   !isSelected && selectedGenres.length >= 5
                     ? 'cursor-not-allowed opacity-50'
                     : 'hover:scale-[1.02]',
                   shouldPulse && 'border-4 accent-border-primary'
                 )}
                 style={{
                   backgroundImage: `url(${imagePath})`,
                   backgroundSize: 'cover',
                   backgroundPosition: 'center',
                   ...(shouldPulse && {
                     animation: 'accent-pulse-glow 2s ease-in-out infinite'
                   })
                 }}
               >
                 <div
                   className={cn(
                     'absolute inset-0 rounded-xl transition-colors',
                     isPrimary
                       ? 'border-4 border-[var(--accent-primary)]/60'
                       : isSelected
                         ? 'border-2 border-[var(--accent-hover)]/60'
                         : 'border border-transparent'
                   )}
                 />
                 <div className="relative z-10 flex h-full flex-col justify-between">
                   <div className="space-y-3">
                     <div className="flex items-center justify-between">
                       <div className="flex items-center gap-2">
                         {/* Exception: Image backgrounds don't change with theme */}
                         <h3
                           className={cn(
                             'capitalize text-white',
                             isPrimary ? 'text-xl font-semibold' : 'text-lg font-medium'
                           )}
                           style={{ textShadow: '0 2px 4px rgba(0,0,0,0.8)' }}
                         >
                           {genre.replace('-', '')}
                         </h3>
                         {isSelected && selectionIndex >= 0 && (
                           <span
                             className={cn(
                               'flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold',
                               isPrimary
                                 ? 'accent-bg-primary text-[var(--color-text-on-accent)] text-sm h-7 w-7'
                                 : 'accent-bg-secondary text-[var(--color-text-on-accent)]'
                             )}
                           >
                             {selectionIndex + 1}
                           </span>
                         )}
                       </div>
                         {/* Exception: Image backgrounds don't change with theme */}
                         {isSelected && (
                           <CheckCircle className={cn(isPrimary ? 'h-6 w-6' : 'h-5 w-5', 'text-white')} style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.8))' }} />
                         )}
                     </div>
                         {/* Exception: Image backgrounds don't change with theme */}
                         <p
                           className="text-sm text-white"
                           style={{ textShadow: '0 2px 4px rgba(0,0,0,0.8)' }}
                         >
                           {genreInfo.description}
                         </p>
                   </div>
                   <div className="space-y-2">
                         {/* Exception: Image backgrounds don't change with theme */}
                         <p
                           className="text-xs font-medium text-white"
                           style={{ textShadow: '0 2px 4px rgba(0,0,0,0.8)' }}
                         >
                           Subcategories
                         </p>
                         {/* Exception: Image backgrounds don't change with theme */}
                         <div
                           className="rounded-lg border p-2 text-xs text-white bg-black/30 border-white/20"
                           style={{ textShadow: '0 2px 4px rgba(0,0,0,0.8)' }}
                         >
                           {genreInfo.subcategories.join(' •')}
                         </div>
                   </div>
                   {isPrimary ? (
                     <Badge className="mt-3 w-max border-none px-3 py-1 text-sm font-bold text-[var(--color-text-on-accent)] accent-bg-primary shadow-lg">
                       ⭐ Primary genre
                     </Badge>
                   ) : (
                     isSelected && (
                       <Badge className="mt-3 w-max border-none px-2 py-0.5 text-xs text-[var(--color-text-on-accent)] accent-bg-secondary">
                         Secondary #{selectionIndex + 1}
                       </Badge>
                     )
                   )}
                 </div>
               </button>
             )
           })}
         </div>

         {/* Success message after primary selection */}
         {showSuccessMessage && tipsEnabled && selectedGenres.length === 1 && (
           <div className="mt-4 p-3 bg-theme-bg-elevated border border-theme-border-success rounded-lg animate-in fade-in duration-300">
             <p className="text-sm text-theme-text-primary flex items-center gap-2">
               <CheckCircle className="h-4 w-4 text-theme-text-success" />
               <span>Primary set! Add more genres or continue →</span>
             </p>
           </div>
         )}

         {selectedGenres.length > 0 && (
           <div className="mt-4 rounded-xl border border-theme-border-card p-4 bg-theme-bg-secondary">
             <div className="mb-2 flex items-center gap-2">
               <CheckCircle className="h-5 w-5 accent-text-primary" />
               <p className="font-semibold text-theme-text-primary">
                 Selected Genres ({selectedGenres.length}/5)
               </p>
             </div>
             <div className="flex flex-wrap gap-2">
               {selectedGenres.map((genre, index) => (
                 <Badge
                   key={genre}
                   className={cn(
                     'px-3 py-1 text-xs',
                     index === 0
                       ? 'accent-bg-primary text-[var(--color-text-on-accent)] text-sm font-semibold shadow border-none'
                       : 'bg-theme-bg-elevated border border-theme-border-primary accent-text-primary text-theme-text-primary'
                   )}
                 >
                   {index === 0 ? '⭐' : `${index + 1}. `}
                   {genre}
                 </Badge>
               ))}
             </div>
             <p className="mt-2 text-xs text-theme-text-secondary">
               Tip: Click any badge to remove that genre. ⭐ Primary sets the story&apos;s overall tone.
             </p>
           </div>
         )}

         {!genreComplete && (
           <p className="text-sm text-theme-text-primary">Please select at least one genre to continue.</p>
         )}
       </div>
     ),
     isComplete: genreComplete
   },
  {
    id: 2,
    title: 'Shape Your Story',
    description: 'Fine-tune the balance of plot, characters, world, and style.',
    content: (
      <div className="space-y-8">
        {/* Story Foundation - 4 sliders */}
        <div>
          <h3 className="text-lg font-semibold text-theme-text-primary mb-4">Story Foundation</h3>
          <StoryFoundationSection
            showHeader={false}
            values={{
              plot_focus: preferences.plot_focus,
              character_focus: preferences.character_focus,
              worldbuilding_depth: preferences.worldbuilding_depth,
              theme_focus: preferences.theme_focus
            }}
            onChange={handlePreferenceChange}
          />
        </div>

        {/* Divider */}
        <div className="border-t border-theme-border-primary" />

        {/* Writing Style - 2 sliders */}
        <div>
          <h3 className="text-lg font-semibold text-theme-text-primary mb-4">Writing Style</h3>
          <WritingStyleSection
            showHeader={false}
            values={{
              tone: preferences.tone,
              pacing: preferences.pacing
            }}
            onChange={handlePreferenceChange}
          />
        </div>

        <InfoTip icon={<Sparkles className="h-4 w-4" />}>
          You can return to tweak these later. Many creators refine the foundation once early chapters are written.
        </InfoTip>
      </div>
    ),
    isComplete: shapeComplete
  },
  {
    id: 3,
    title: 'Create Your Story',
    description: 'Give your story a title and premise, or let AI help you brainstorm.',
     content: (
       <div className="space-y-6">
         <InfoTip icon={<Sparkles className="h-4 w-4" />}>
           Click Generate Ideas to fetch five story suggestions; choosing one will instantly populate both the title and premise fields—perfect for jump-starting a new concept. You can generate new ideas multiple times—they will replace the previous suggestions.
         </InfoTip>

         <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center">
           {selectedGenres.length === 0 && (
             <p className="flex items-center gap-1 text-xs text-theme-text-secondary">
               <AlertCircle className="h-3 w-3 accent-text-primary" />
               Select a genre first
             </p>
           )}
           <InstrumentedButton
             type="button"
             size="lg"
             eventName="story.create.generate_ideas"
             component="create-page"
             errorMessage="Failed to generate story ideas"
             onClick={instrumentedGenerateIdeas}
             disabled={selectedGenres.length === 0 || generatingIdeas}
             className={cn(
               'gap-3 rounded-2xl px-8 py-3 text-base font-semibold shadow-lg transition-transform backdrop-blur-xl border border-theme-border-primary/20',
               accentButton,
               selectedGenres.length === 0 || generatingIdeas
                 ? 'cursor-not-allowed opacity-60'
                 : 'hover:scale-[1.05] hover:shadow-2xl'
             )}
             title={`Generate 5 unique story premise ideas based on your selected genres. Click on any idea to use it as your story premise.${
               ideaGenerationCost > 0
                 ? ` Last generation cost: ${ideaGenerationCost} credits ($${(ideaGenerationCost * 0.001).toFixed(3)})`
                 : ''
             }${selectedGenres.length === 0 ? ' (Select genres first)' : ''}`}
           >
             {generatingIdeas ? (
               <>
                 <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                 Generating...
               </>
             ) : (
               <>
                 <Sparkles className="mr-2 h-5 w-5" />
                 Generate Ideas
                 {ideaGenerationCost > 0 && (
                   <span className="ml-1 text-sm opacity-80">(~{ideaGenerationCost}c)</span>
                 )}
               </>
             )}
           </InstrumentedButton>
         </div>

         <div className="space-y-6">
           <div>
             <Label htmlFor="title" className="font-medium text-theme-text-primary">
               Story Title *
             </Label>
             <Input
               id="title"
               type="text"
               placeholder="Enter your story title..."
               value={formData.title}
               onChange={(e) => handleInputChange('title', e.target.value)}
               className="mt-2 bg-theme-bg-tertiary border border-theme-border-primary text-theme-text-primary placeholder:text-theme-text-muted"
             />
           </div>

           <div>
             <Label htmlFor="premise" className="font-medium text-theme-text-primary">
               Story Premise *
             </Label>
             <Textarea
               id="premise"
               placeholder="Describe your story idea, characters, setting, and main plot..."
               value={formData.premise}
               onChange={(e) => handleInputChange('premise', e.target.value)}
               className="mt-2 min-h-[400px] bg-theme-bg-tertiary border border-theme-border-primary text-theme-text-primary placeholder:text-theme-text-muted"
             />
           </div>
         </div>

         {generatingIdeas && (
           <div className="mt-4 rounded-xl border border-theme-border-card p-4 bg-theme-bg-secondary">
             <div className="flex items-center gap-2 text-theme-text-secondary">
               <Loader2 className="h-5 w-5 animate-spin accent-text-primary" />
               Generating story ideas using AI...
             </div>
           </div>
         )}

         {storyIdeas.length > 0 && (
           <div className="mt-4 space-y-2">
             <div className="flex items-center justify-between">
               <p className="text-sm font-medium text-theme-text-primary">
                 Generated Story Ideas ({storyIdeas.length})
               </p>
               <p className="text-xs text-theme-text-secondary">✓ Click any idea to use it</p>
             </div>
             <div className="space-y-2">
               {storyIdeas.map((idea, index) => (
                 <div
                   key={index}
                   className="bg-theme-bg-secondary border border-theme-border-card cursor-pointer rounded-xl p-3 transition-all hover:scale-[1.02] hover:shadow-lg"
                   onClick={() => instrumentedIdeaSelect(idea)}
                   onKeyDown={(e) => {
                     if (e.key === 'Enter' || e.key === '') {
                       e.preventDefault()
                       instrumentedIdeaSelect(idea)
                     }
                   }}
                   role="button"
                   tabIndex={0}
                   title="Click to use this idea as your story premise"
                 >
                   <h3 className="text-sm font-semibold text-theme-text-primary">
                     {index + 1}. {idea.title}
                   </h3>
                   <p className="mt-2 text-sm leading-relaxed text-theme-text-secondary">{idea.premise}</p>
                   {idea.hook && (
                     <p className="mt-2 text-xs opacity-80 text-theme-text-secondary">✨ {idea.hook}</p>
                   )}
                 </div>
               ))}
             </div>
           </div>
         )}
       </div>
     ),
     isComplete: storyDetailsComplete
   },
  {
    id: 4,
    title: 'Set Your Story Length',
    description: 'Choose your chapter count and length preferences.',
     content: (
       <div className="space-y-6">
         <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
           <div>
             <Label htmlFor="target_chapters" className="font-medium text-theme-text-primary">
               Target Chapters
             </Label>
             <Input
               id="target_chapters"
               type="number"
               min="1"
               max="100"
               value={formData.target_chapters}
               onChange={(e) => handleInputChange('target_chapters', parseInt(e.target.value) || 1)}
               className="mt-2 bg-theme-bg-tertiary border border-theme-border-primary text-theme-text-primary placeholder:text-theme-text-muted"
             />
           </div>

           <div>
             <Label htmlFor="target_length" className="font-medium text-theme-text-primary">
               Overall Story Length
             </Label>
             <Select value={formData.target_length} onValueChange={handleTargetLengthChange}>
               <SelectTrigger id="target_length" className="mt-2 bg-theme-bg-tertiary border border-theme-border-primary text-theme-text-primary">
                 <SelectValue placeholder="Select target length" />
               </SelectTrigger>
               <SelectContent className="bg-theme-bg-secondary border-theme-border-card">
                 <SelectItem value="short">Short (1-5 chapters)</SelectItem>
                 <SelectItem value="medium">Medium (10-20 chapters)</SelectItem>
                 <SelectItem value="long">Long (21+ chapters)</SelectItem>
               </SelectContent>
             </Select>
           </div>

           <div>
             <Label htmlFor="target_chapter_length" className="font-medium text-theme-text-primary">
               Target Chapter Length
             </Label>
             <Select
               value={formData.target_chapter_length}
               onValueChange={(value) => handleInputChange('target_chapter_length', value)}
             >
               <SelectTrigger id="target_chapter_length" className="mt-2 bg-theme-bg-tertiary border border-theme-border-primary text-theme-text-primary">
                 <SelectValue placeholder="Select chapter length" />
               </SelectTrigger>
               <SelectContent className="bg-theme-bg-secondary border-theme-border-card">
                 <SelectItem value="short">Short (500-1000 words)</SelectItem>
                 <SelectItem value="medium">Medium (1000-2500 words)</SelectItem>
                 <SelectItem value="long">Long (2500+ words)</SelectItem>
               </SelectContent>
             </Select>
           </div>
         </div>

         <div className="mt-4 rounded-2xl border border-theme-border-card p-4 shadow-xl accent-border-primary/40 bg-theme-bg-secondary">
           <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
             <div>
               <p className="text-sm font-semibold text-theme-text-primary">Estimated Cost</p>
               <p className="text-sm text-theme-text-primary">
                 ~{estimatedCost} credits (${(estimatedCost * 0.001).toFixed(2)})
               </p>
             </div>
             <div className="text-sm text-right">
               <p className={balanceAfter < 0 ? 'text-theme-text-error' : 'text-theme-text-secondary'}>
                 Your balance after: {Math.max(balanceAfter, 0)} credits
               </p>
               {balanceAfter < 0 && (
                 <p className="text-xs text-theme-text-error opacity-80">
                   You'll need {Math.abs(balanceAfter)} additional credits to complete this request.
                 </p>
               )}
             </div>
           </div>
         </div>
       </div>
     ),
     isComplete: lengthComplete
   }
 ]

 // Create header with tabs
 const wizardHeader = (
   <div className="space-y-4">
     <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
       <div>
         <h1 className="text-2xl font-bold flex items-center gap-2 text-theme-text-primary">
           <Wand2 className="h-6 w-6 accent-text-primary" />
           Create New Story
         </h1>
         <p className="text-sm text-theme-text-secondary">Choose your story type and let AI help you build your narrative</p>
       </div>
       <div className="flex items-center gap-3">
         <button
           onClick={instrumentedNavigateSettings}
           className={cn('flex items-center gap-2 px-4 py-2 rounded-lg transition-all text-sm font-medium', accentButton)}
         >
           <Settings className="h-4 w-4" />
           Settings
         </button>
         <ThemeToggle />
       </div>
     </div>

     <Tabs value={activeTab} onValueChange={(value) => handleTabChange(value as CreationMode)}>
       <div className="border-b-2 border-theme-border-primary">
         <div className="flex gap-2 sm:gap-3 overflow-x-auto pb-1">
           {[
             { value: 'novel', label: 'Novel', icon: BookOpen, enabled: true },
             { value: 'choice-book', label: 'Choice Book', icon: TreePine, enabled: false }
           ].map(tab => (
             <button
               key={tab.value}
               onClick={() => tab.enabled && instrumentedTabToggle(tab.value as CreationMode)}
               disabled={!tab.enabled}
               className={cn(
                 'flex items-center gap-2 whitespace-nowrap rounded-xl px-6 py-3 text-base font-semibold transition-all duration-200',
                 'bg-theme-bg-secondary text-theme-text-primary',
                 tab.enabled ? 'hover:scale-[1.03] hover:shadow-xl' : '',
                 !tab.enabled && 'text-theme-text-secondary cursor-not-allowed opacity-50',
                 tab.enabled && activeTab === tab.value
                   ? 'accent-bg-primary text-[var(--color-text-on-accent)] shadow-2xl scale-105 border border-transparent'
                   : tab.enabled
                     ? 'border border-transparent'
                     : ''
               )}
             >
               <tab.icon className="h-4 w-4" />
               {tab.label}
               {!tab.enabled && <span className="text-xs ml-1">(Coming Soon)</span>}
             </button>
           ))}
         </div>
       </div>
     </Tabs>

     {activeTab === 'novel' && (
       <div className="mb-2">
         <h2 className="text-xl font-semibold mb-1 text-theme-text-primary">{tabConfig.title}</h2>
         <p className="text-sm text-theme-text-secondary">{tabConfig.description}</p>
       </div>
     )}

     {error && (
       <div className="p-4 border border-theme-border-error text-theme-text-error bg-theme-bg-error rounded-xl">
         <div className="flex items-center gap-2">
           <AlertCircle className="h-5 w-5 accent-text-primary" />
           {error}
         </div>
       </div>
      )}
    </div>
  )

  return (
    <AppErrorBoundary source="create-story-page">
      {/* Monitoring boundary ensures ErrorBoundary coverage from'@/components/common/ErrorMonitoringV2' */}
      <div className="relative min-h-screen overflow-hidden">
        {renderBackgroundLayers()}

        {/* Content */}
        <div className="relative z-20 min-h-screen flex flex-col">
          <div className="container mx-auto px-4 py-8 lg:py-8 max-lg:px-0 max-lg:py-0">
            {activeTab === 'novel' ? (
              <div className="shadow-2xl bg-theme-bg-secondary border border-theme-border-card rounded-xl max-lg:rounded-none max-lg:min-h-screen max-lg:border-0">
                <FullscreenWizard
                  steps={wizardSteps}
                  currentStep={currentStep}
                  onStepChange={setCurrentStep}
                  onComplete={instrumentedSubmit}
                  isSubmitting={isSubmitting}
                  submitLabel={`Create ${tabConfig.title}`}
                  header={wizardHeader}
                />
              </div>
            ) : (
              <div className="flex items-center justify-center p-4">
                <div className="text-center py-12 bg-theme-bg-secondary border border-theme-border-card rounded-xl p-8 max-w-md">
                  <TreePine className="h-16 w-16 mx-auto mb-4 text-theme-text-primary" />
                  <h3 className="text-2xl font-semibold mb-2 text-theme-text-primary">
                    Choice Books Coming Soon
                  </h3>
                  <p className="text-theme-text-secondary">
                    Interactive stories where readers make choices that affect the outcome are in development.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Confirmation Dialog for Regenerating Ideas */}
        <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Regenerate Story Ideas?</DialogTitle>
              <DialogDescription>
                You already have {storyIdeas.length} story idea{storyIdeas.length !== 1 ? 's' : ''} generated.
                Generating new ideas will replace the current ones. Are you sure you want to continue?
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleCancelGenerate}
                disabled={generatingIdeas}
                className="border-theme-border-card hover:bg-theme-bg-secondary"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleConfirmGenerate}
                disabled={generatingIdeas}
                className={cn(accentButton, 'font-semibold')}
              >
                {generatingIdeas ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  'Generate New Ideas'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppErrorBoundary>
  )
 }
